/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from '@tanstack/react-query';
import {apiClient} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';
import {STALE_TIME, GC_TIME} from '#constants';
import {ENDPOINTS} from '@/src/utils/api';
import {
  getBankAccountResponseTransformer,
  getBenefitUsageRateResponseTransformer,
  getCashbackOverviewResponseTransformer,
  getCashbackWithdrawalHistoryResponseTransformer,
  getFundsFlowOverviewResponseTransformer,
  getFundsFlowResponseTransformer,
  getInvoiceHistoryResponseTransformer,
  getInvoiceResponseTransformer,
  getPerformanceResponseTransformer,
  getSimplePerformanceByCategoryResponseTransformer,
  getSimplePerformanceResponseTransformer,
  getTotalRedemptionResponseTransformer
} from '@/src/utils/response-transformers/cashback';
import {USER_ROLES} from '@/src/utils/constants';
import {TFunction} from '@/src/i18n/types';

// Hook for fetching current user cashback balance
export function useGetUserCashbackBalance(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'balance', 'user'],
    queryFn: () => apiClient.get(ENDPOINTS.cashback.user.balance),
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current user cashback cards
export function useGetUserCashbackUsedCards(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'used-cards', 'user'],
    queryFn: () => apiClient.get(ENDPOINTS.cashback.user.usedCards),
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current organisation cashback balance
export function useGetOrganisationCashbackBalance(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'balance', 'organisation'],
    queryFn: () => apiClient.get(ENDPOINTS.cashback.organisation.balance),
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current user cashback balance
export function useGetUserCashbackTotal(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'total', 'user'],
    queryFn: () => apiClient.get(ENDPOINTS.cashback.user.total),
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current organisation cashback balance
export function useGetOrganisationCashbackTotal(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'total', 'organisation'],
    queryFn: () => apiClient.get(ENDPOINTS.cashback.organisation.balance),
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current user pending cashback
export function useGetUserPendingCashback(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'pending', 'user'],
    queryFn: () => apiClient.get(ENDPOINTS.cashback.user.pending),
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current organisation cashback statistics
export function useGetOrganisationCashbackStatistics(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'statistics', 'organisation'],
    queryFn: () => apiClient.get(ENDPOINTS.cashback.organisation.statistics),
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current user performance
export function useGetUserCashbackPerformance(
  params: {
    type: Omit<USER_ROLES, 'member'>;
    startDate?: string;
    endDate?: string;
  } = {
    type: USER_ROLES.merchant
  },
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'performance', 'user'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.user.performance,
        {params}
      );
      return getSimplePerformanceResponseTransformer(
        (response as any)?.data as any
      );
    },
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current user performance
export function useGetUserCashbackPerformanceByCategory(
  params: {
    startDate?: string;
    endDate?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'performance-category', 'user'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.user.performanceByCategory,
        {params}
      );
      return getSimplePerformanceByCategoryResponseTransformer(
        (response as any)?.data as any
      );
    },
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current organisation performance
export function useGetOrganisationCashbackPerformance(
  locale,
  params: {
    startDate?: string;
    endDate?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.cashback.general(),
      'performance',
      'organisation',
      params.toString()
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.organisation.performance,
        {params}
      );
      // @ts-ignore
      return getPerformanceResponseTransformer(response?.data, locale);
    },
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current organisation performance
export function useGetOrganisationCashbackPerformanceByCategory(
  params: {
    startDate?: string;
    endDate?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.cashback.general(),
      'performance-category',
      'organisation'
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.organisation.performanceByCategory,
        {params}
      );
      // @ts-ignore
      return getSimplePerformanceByCategoryResponseTransformer(response?.data);
    },
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current organisation benefit usage rate
export function useGetOrganisationBenefitUsageRate(
  locale,
  params: {
    startDate?: string;
    endDate?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.cashback.general(),
      'benefit-usage-rate',
      'organisation'
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.organisation.benefitUsageRate,
        {params}
      );
      // @ts-ignore
      return getBenefitUsageRateResponseTransformer(response?.data, locale);
    },
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current merchant performance
export function useGetMerchantCashbackPerformance(
  locale,
  params: {
    startDate?: string;
    endDate?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'performance', 'merchant'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.merchant.performance,
        {params}
      );
      return getPerformanceResponseTransformer((response as any)?.data, locale);
    },
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current merchant performance
export function useGetMerchantCashbackPerformanceByCategory(
  locale,
  params: {
    startDate?: string;
    endDate?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.cashback.general(),
      'performance-category',
      'merchant'
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.merchant.performanceByCategory,
        {params}
      );
      return getSimplePerformanceByCategoryResponseTransformer(
        (response as any)?.data
      );
    },
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current merchant cashback statistics
export function useGetMerchantCashbackStatistics(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'statistics', 'merchant'],
    queryFn: () => apiClient.get(ENDPOINTS.cashback.merchant.statistics),
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for fetching current merchant total redemption
export function useGetMerchantTotalRedemption(
  params: {
    startDate?: string;
    endDate?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'total-redemption', 'merchant'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.merchant.redemptionRate,
        {params}
      );
      return getTotalRedemptionResponseTransformer((response as any)?.data);
    },
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    ...options
  });
}

// Hook for downloading user withdrawal history
export function useDownloadUserWithdrawalHistory(t: TFunction) {
  return useMutation({
    mutationFn: async () => {
      const blob = await apiClient.post(
        ENDPOINTS.cashback.user.withdrawalHistoryDownload,
        undefined,
        undefined,
        true
      );

      // const contentDisposition = response?.headers?.get('Content-Disposition');
      const filename = `${t('CashbackPage.Table.title').toLowerCase().replaceAll(' ', '-')}.csv`;

      // if (contentDisposition) {
      //   const match = contentDisposition.match(/filename="(.+)"/);
      //   if (match?.[1]) {
      //     filename = match[1];
      //   }
      // }

      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  });
}

// Hook for downloading user overview
export function useDownloadUserOverview(t: TFunction) {
  return useMutation({
    mutationFn: async (data: {
      startDate?: string;
      endDate?: string;
      cardNumber?: string;
      search?: string;
      sortBy?: string;
      sortType?: string;
    }) => {
      const params = new URLSearchParams();

      if (data.startDate) params.append('startDate', data.startDate);
      if (data.endDate) params.append('endDate', data.endDate);
      if (data.cardNumber) params.append('cardNumber', data.cardNumber);
      if (data.search) params.append('search', data.search);
      if (data.sortBy) params.append('sortBy', data.sortBy);
      if (data.sortType) params.append('sortType', data.sortType);

      const blob = await apiClient.post(
        `${ENDPOINTS.cashback.user.overviewDownload}?${params.toString()}`,
        undefined,
        undefined,
        true
      );

      // const contentDisposition = response?.headers?.get('Content-Disposition');
      const filename = `${t('CashbackOverviewPage.Table.title').toLowerCase().replaceAll(' ', '-')}.csv`;

      // if (contentDisposition) {
      //   const match = contentDisposition.match(/filename="(.+)"/);
      //   if (match?.[1]) {
      //     filename = match[1];
      //   }
      // }

      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  });
}

// Hook for downloading organisation withdrawal history
export function useDownloadOrganisationWithdrawalHistory(t: TFunction) {
  return useMutation({
    mutationFn: async () => {
      const blob = await apiClient.post(
        ENDPOINTS.cashback.organisation.withdrawalHistoryDownload,
        undefined,
        undefined,
        true
      );

      // const contentDisposition = response?.headers?.get('Content-Disposition');
      const filename = `${t('CashbackPage.Table.title').toLowerCase().replaceAll(' ', '-')}.csv`;

      // if (contentDisposition) {
      //   const match = contentDisposition.match(/filename="(.+)"/);
      //   if (match?.[1]) {
      //     filename = match[1];
      //   }
      // }

      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  });
}

// Hook for downloading organisation overview
export function useDownloadOrganisationOverview(t: TFunction) {
  return useMutation({
    mutationFn: async () => {
      const blob = await apiClient.post(
        ENDPOINTS.cashback.organisation.overviewDownload,
        undefined,
        undefined,
        true
      );

      // const contentDisposition = response?.headers?.get('Content-Disposition');
      const filename = `${t('CashbackOverviewPage.Table.title').toLowerCase().replaceAll(' ', '-')}.csv`;

      // if (contentDisposition) {
      //   const match = contentDisposition.match(/filename="(.+)"/);
      //   if (match?.[1]) {
      //     filename = match[1];
      //   }
      // }

      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  });
}

// Hook for downloading merchant invoice history
export function useDownloadMerchantInvoiceHistory(t: TFunction) {
  return useMutation({
    mutationFn: async () => {
      const blob = await apiClient.post(
        ENDPOINTS.cashback.merchant.invoiceHistoryDownload,
        undefined,
        undefined,
        true
      );

      // const contentDisposition = response?.headers?.get('Content-Disposition');
      const filename = `${t('FinancesPage.invoices.title').toLowerCase().replaceAll(' ', '-')}.csv`;

      // if (contentDisposition) {
      //   const match = contentDisposition.match(/filename="(.+)"/);
      //   if (match?.[1]) {
      //     filename = match[1];
      //   }
      // }

      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  });
}

// Hook for downloading merchant invoice history
export function useDownloadMerchantInvoice(t: TFunction) {
  return useMutation({
    mutationFn: async (data: {id: number | string}) => {
      const blob = await apiClient.post(
        ENDPOINTS.cashback.merchant.invoiceDownload(data?.id),
        undefined,
        undefined,
        true
      );

      // const contentDisposition = response?.headers?.get('Content-Disposition');
      const filename = `${t('InvoicePage.breakdown.title').toLowerCase().replaceAll(' ', '-')}.csv`;

      // if (contentDisposition) {
      //   const match = contentDisposition.match(/filename="(.+)"/);
      //   if (match?.[1]) {
      //     filename = match[1];
      //   }
      // }

      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  });
}

// Hook for withdrawing user cashback
export function useWithdrawUserCashback() {
  return useMutation({
    mutationFn: async (data: {amount: number}) =>
      apiClient.post(ENDPOINTS.cashback.user.withdraw, data)
  });
}

// Hook for withdrawing organisation cashback
export function useWithdrawOrganisationCashback() {
  return useMutation({
    mutationFn: async (data: {amount: number}) =>
      apiClient.post(ENDPOINTS.cashback.organisation.withdraw, data)
  });
}

// Hook for fetching organisation funds flow list
export function useGetOrganisationFundsFlow(
  params: {
    limit?: number;
    pageNumber?: number;
    startDate?: string | Date;
    endDate?: string | Date;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.fundsFlow(params), 'organisation'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.organisation.fundsFlow,
        params
      );
      return getFundsFlowResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.NOTIFICATIONS,
    gcTime: GC_TIME.NOTIFICATIONS,
    ...options
  });
}

// Hook for downloading user overview
export function useDownloadOrganisationFundsFlow(t: TFunction) {
  return useMutation({
    mutationFn: async (data: {startDate?: string; endDate?: string}) => {
      const blob = await apiClient.post(
        `${ENDPOINTS.cashback.organisation.fundsFlowDownload}?startDate=${data.startDate || ''}&endDate=${data.endDate || ''}`,
        undefined,
        undefined,
        true
      );

      // const contentDisposition = response?.headers?.get('Content-Disposition');
      const filename = `${t('FinancesPage.funds.flow').toLowerCase().replaceAll(' ', '-')}.csv`;

      // if (contentDisposition) {
      //   const match = contentDisposition.match(/filename="(.+)"/);
      //   if (match?.[1]) {
      //     filename = match[1];
      //   }
      // }

      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  });
}

// Hook for fetching user overview list
export function useGetUserCashbackOverview(
  params: {
    limit?: number;
    pageNumber?: number;
    statuses?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    sortBy?: string;
    cardNumber?: string;
    sortType?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.overview(params), 'user'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.user.overview,
        params
      );
      return getCashbackOverviewResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.NOTIFICATIONS,
    gcTime: GC_TIME.NOTIFICATIONS,
    ...options
  });
}

// Hook for fetching organisation overview list
export function useGetOrganisationCashbackOverview(
  params: {
    limit?: number;
    pageNumber?: number;
    date?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.overview(params), 'organisation'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.organisation.overview,
        params
      );
      return getFundsFlowOverviewResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.NOTIFICATIONS,
    gcTime: GC_TIME.NOTIFICATIONS,
    ...options
  });
}

// Hook for fetching user withdrawal history list
export function useGetUserCashbackWithdrawalHistory(
  params: {
    limit?: number;
    pageNumber?: number;
    statuses?: string;
    startDate?: string | Date;
    endDate?: string | Date;
    sortBy?: string;
    sortType?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.withdrawalHistory(params), 'user', params],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.user.withdrawalHistory,
        params
      );
      return getCashbackWithdrawalHistoryResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.NOTIFICATIONS,
    gcTime: GC_TIME.NOTIFICATIONS,
    ...options
  });
}

// Hook for fetching organisation withdrawal history list
export function useGetOrganisationCashbackWithdrawalHistory(
  params: {
    limit?: number;
    pageNumber?: number;
    statuses?: string;
    startDate?: string | Date;
    endDate?: string | Date;
    sortBy?: string;
    sortType?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.cashback.withdrawalHistory(params),
      'organisation',
      params
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.organisation.withdrawalHistory,
        params
      );
      return getCashbackWithdrawalHistoryResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.NOTIFICATIONS,
    gcTime: GC_TIME.NOTIFICATIONS,
    ...options
  });
}

// Hook for searching user overview
export function useSearchUserOverview(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.cashback.overview(params),
      'search',
      'user',
      params
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.user.overview,
        params
      );
      return getCashbackOverviewResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

// Hook for searching organisation overview
export function useSearchOrganisationOverview(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.cashback.overview(params),
      'search',
      'organisation',
      params
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.organisation.overview,
        params
      );
      return getCashbackOverviewResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

// Hook for searching merchant invoices
export function useSearchMerchantInvoices(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.cashback.invoiceHistory(params),
      'search',
      'merchant',
      params
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.merchant.invoiceHistory,
        params
      );
      return getInvoiceHistoryResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

// Hook for fetching merchant invoice list
export function useGetMerchantInvoices(
  params: {
    limit?: number;
    pageNumber?: number;
    statuses?: string[];
    startDate?: string;
    endDate?: string;
    search?: string;
    sortBy?: string;
    sortType?: string;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.invoiceHistory(params), 'merchant'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.merchant.invoiceHistory,
        params
      );
      return getInvoiceHistoryResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.NOTIFICATIONS,
    gcTime: GC_TIME.NOTIFICATIONS,
    ...options
  });
}

// Hook for fetching merchant invoice by id
export function useGetMerchantInvoice(
  params: {
    id: number;
  },
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'invoice', 'merchant'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.merchant.invoice(params?.id)
      );
      return getInvoiceResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.NOTIFICATIONS,
    gcTime: GC_TIME.NOTIFICATIONS,
    ...options
  });
}

// Hook for marking merchant invoice by id
export function useMarkMerchantInvoiceAsPaid(params: {id: number}) {
  return useMutation({
    mutationFn: () =>
      apiClient.put(ENDPOINTS.cashback.merchant.markInvoiceAsPaid(params?.id))
  });
}

// Hook for fetching merchant new invoice notification
export function useGetMerchantNewInvoiceNotification(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.general(), 'new-invoice', 'merchant'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.merchant.newInvoice
      );
      return response as any;
    },
    staleTime: STALE_TIME.NOTIFICATIONS,
    gcTime: GC_TIME.NOTIFICATIONS,
    ...options
  });
}

// Hook for fetching organisation bank accounts
export function useGetOrganisationBankAccounts(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.cashback.bankAccount({}), 'organisation'],
    queryFn: async () => {
      const response = await apiClient.get(
        ENDPOINTS.cashback.organisation.bankAccount
      );
      // @ts-ignore
      return getBankAccountResponseTransformer(response?.data?.data);
    },
    staleTime: STALE_TIME.NOTIFICATIONS,
    gcTime: GC_TIME.NOTIFICATIONS,
    ...options
  });
}

// Hook for creating organisation bank account
export function useCreateOrganisationBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {name: string; accountNumber: string}) =>
      apiClient.post(ENDPOINTS.cashback.organisation.createBankAccount, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cashback.bankAccount({})
      });
    }
  });
}

// Hook for updating organisation bank account
export function useUpdateOrganisationBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      accountNumber: string;
      id: number;
    }) =>
      apiClient.put(
        ENDPOINTS.cashback.organisation.updateBankAccount(data.id),
        {
          name: data.name,
          accountNumber: data.accountNumber
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cashback.bankAccount({})
      });
    }
  });
}

// Hook for deleting organisation bank account
export function useDeleteOrganisationBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {id: number}) =>
      apiClient.delete(
        ENDPOINTS.cashback.organisation.updateBankAccount(data.id)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cashback.bankAccount({})
      });
    }
  });
}

// Hook for downloading merchant usage data csv example
export function useDownloadMerchantUsageDataCsvExample(t: TFunction) {
  return useMutation({
    mutationFn: async () => {
      const blob = await apiClient.post(
        ENDPOINTS.cashback.merchant.downloadUsageCsvExample,
        undefined,
        undefined,
        true
      );

      const filename = `${t('FinancesPage.usageData.title').toLowerCase().replaceAll(' ', '-')}-ex.csv`;

      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  });
}

// Hook for uploading usage data csv
export function useUploadMerchantUsageDataCsv() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formData,
      startDate,
      endDate
    }: {
      formData: FormData;
      startDate: string;
      endDate: string;
    }) =>
      apiClient.post(
        `${ENDPOINTS.cashback.merchant.uploadUsageCsv}?startDate=${startDate}&endDate=${endDate}`,
        formData,
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.cashback.general(), 'new-invoice', 'merchant']
      });
    }
  });
}
